import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const EventProgramSection = () => {
  const handleDownloadAgenda = () => {
    const filePath = '/assets/files/المؤتمر العام الثاني - برنامج المؤتمر التفصيلي.pdf';
    const link = document.createElement('a');
    link.href = filePath;
    link.download = 'المؤتمر العام الثاني - برنامج المؤتمر التفصيلي.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const day1Sessions = [
    {
      id: 'opening',
      title: 'الجلسة الافتتاحية والتقارير',
      description: 'استعراض التقرير المالي والإداري وإنجازات الدورة (2024-2025) ترسيخاً لمبدأ الشفافية.',
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'workshops',
      title: 'ورش العمل التخصصية',
      description: 'جلسات مركزة حول "العمل النقابي المؤسسي" لرفع سوية الكوادر.',
      icon: 'Users',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      id: 'debates',
      title: 'منصة المرشحين (المناظرات)',
      description: 'استعراض البرامج الانتخابية للمرشحين والإجابة على استفسارات الهيئة الناخبة.',
      icon: 'Mic',
      color: 'text-success',
      bgColor: 'bg-success/10'
    }
  ];

  const day2Sessions = [
    {
      id: 'bylaws',
      title: 'جلسة النظام الداخلي',
      description: 'مناقشة وإقرار التعديلات القانونية لتطوير بنية الاتحاد.',
      icon: 'FileCheck',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'elections',
      title: 'الجلسات الانتخابية (3 جولات)',
      description: 'انتخاب مجلس الإدارة.\nانتخاب الهيئة الرقابية.\nانتخاب رئاسة الهيئة العمومية.',
      icon: 'Vote',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      subItems: [
        'انتخاب مجلس الإدارة',
        'انتخاب الهيئة الرقابية',
        'انتخاب رئاسة الهيئة العمومية'
      ]
    },
    {
      id: 'results',
      title: 'النتائج والمراسم الختامية',
      description: 'الفرز العلني للأصوات، إعلان التشكيلة القيادية الجديدة، وتسليم المهام رسمياً.',
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    }
  ];

  return (
    <section id="event-program" className="relative py-16 lg:py-24 bg-gradient-to-b from-background via-[#114845]/5 to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6" dir="rtl">
            برنامج المؤتمر: يومان لصياغة المستقبل
          </h2>
        </motion.div>

        {/* Day 1 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <div className="bg-card/80 backdrop-blur-sm border border-border rounded-2xl p-6 md:p-8 mb-8 shadow-memorial">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                <Icon name="Calendar" size={24} className="text-primary" />
              </div>
              <div className="flex-1" dir="rtl">
                <h3 className="font-memorial text-2xl md:text-3xl font-bold text-primary mb-1">
                  اليوم الأول: السبت 31 يناير 2026
                </h3>
                <p className="font-body text-base md:text-lg text-text-secondary">
                  (يوم العرض والنقاش)
                </p>
              </div>
            </div>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

            <div className="space-y-8">
              {day1Sessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Timeline Dot */}
                  <div className={`absolute left-6 w-4 h-4 ${session.bgColor} border-2 border-background rounded-full flex items-center justify-center`}>
                    <div className={`w-2 h-2 ${session.color.replace('text-', 'bg-')} rounded-full`}></div>
                  </div>

                  {/* Session Card */}
                  <div className="ml-16 bg-card border border-border rounded-lg shadow-memorial overflow-hidden hover:shadow-lg transition-all duration-300">
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 ${session.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon name={session.icon} size={24} className={session.color} />
                        </div>
                        <div className="flex-1" dir="rtl">
                          <h4 className="font-memorial text-xl md:text-2xl font-bold text-primary mb-3">
                            {session.title}
                          </h4>
                          <p className="font-body text-base text-text-primary leading-relaxed whitespace-pre-line">
                            {session.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Day 2 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="bg-card/80 backdrop-blur-sm border border-border rounded-2xl p-6 md:p-8 mb-8 shadow-memorial">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                <Icon name="Calendar" size={24} className="text-accent" />
              </div>
              <div className="flex-1" dir="rtl">
                <h3 className="font-memorial text-2xl md:text-3xl font-bold text-primary mb-1">
                  اليوم الثاني: الأحد 01 فبراير 2026
                </h3>
                <p className="font-body text-base md:text-lg text-text-secondary">
                  (يوم الاقتراع والقرار)
                </p>
              </div>
            </div>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

            <div className="space-y-8">
              {day2Sessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="relative"
                >
                  {/* Timeline Dot */}
                  <div className={`absolute left-6 w-4 h-4 ${session.bgColor} border-2 border-background rounded-full flex items-center justify-center`}>
                    <div className={`w-2 h-2 ${session.color.replace('text-', 'bg-')} rounded-full`}></div>
                  </div>

                  {/* Session Card */}
                  <div className="ml-16 bg-card border border-border rounded-lg shadow-memorial overflow-hidden hover:shadow-lg transition-all duration-300">
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 ${session.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon name={session.icon} size={24} className={session.color} />
                        </div>
                        <div className="flex-1" dir="rtl">
                          <h4 className="font-memorial text-xl md:text-2xl font-bold text-primary mb-3">
                            {session.title}
                          </h4>
                          <p className="font-body text-base text-text-primary leading-relaxed whitespace-pre-line mb-3">
                            {session.description}
                          </p>
                          {session.subItems && (
                            <ul className="space-y-2 mt-4" dir="rtl">
                              {session.subItems.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start gap-2">
                                  <div className={`w-1.5 h-1.5 ${session.color.replace('text-', 'bg-')} rounded-full mt-2 flex-shrink-0`}></div>
                                  <p className="font-body text-base text-text-primary leading-relaxed">
                                    {item}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Download Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-12 lg:mt-16"
        >
          <button
            onClick={handleDownloadAgenda}
            className="inline-flex items-center gap-3 px-6 py-4 bg-primary hover:bg-primary/90 text-white font-memorial text-lg md:text-xl rounded-lg shadow-memorial hover:shadow-lg transition-all duration-300 transform hover:scale-105"
            dir="rtl"
          >
            <Icon name="Download" size={24} />
            <span>تحميل البرنامج التفصيلي للمؤتمر</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default EventProgramSection;
